import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import AppLayout from '@/components/layouts/AppLayout'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Paper from '@mui/material/Paper'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import OutlinedInput from '@mui/material/OutlinedInput'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Avatar from '@mui/material/Avatar'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import type { AppEntry, Category } from '@/types'

interface Props {
  vendor: { id: number; name: string; slug: string }
  app_entry: AppEntry
  categories: Category[]
}

interface PageProps {
  errors?: Record<string, string[]>
}

export default function AppEntryEdit({ vendor, app_entry, categories }: Props) {
  const { props } = usePage<PageProps>()
  const [form, setForm] = useState({
    name: app_entry.name,
    description: app_entry.description || '',
    short_description: app_entry.short_description || '',
    visibility: app_entry.visibility,
  })
  const [categoryIds, setCategoryIds] = useState<number[]>(app_entry.category_ids || [])
  const [logo, setLogo] = useState<File | null>(null)
  const [screenshots, setScreenshots] = useState<FileList | null>(null)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const formData = new FormData()
    formData.append('app_entry[name]', form.name)
    formData.append('app_entry[description]', form.description)
    formData.append('app_entry[short_description]', form.short_description)
    formData.append('app_entry[visibility]', form.visibility)
    categoryIds.forEach((id) => formData.append('app_entry[category_ids][]', String(id)))
    if (logo) formData.append('app_entry[logo]', logo)
    if (screenshots) {
      Array.from(screenshots).forEach((file) => formData.append('app_entry[screenshots][]', file))
    }
    formData.append('_method', 'patch')
    router.post(`/vendors/${vendor.slug}/app_entries/${app_entry.slug}`, formData)
  }

  const handleDeleteScreenshot = (screenshotId: number) => {
    router.delete(`/vendors/${vendor.slug}/app_entries/${app_entry.slug}/remove_screenshot?screenshot_id=${screenshotId}`)
  }

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this app entry? This action cannot be undone.')) {
      router.delete(`/vendors/${vendor.slug}/app_entries/${app_entry.slug}`)
    }
  }

  const errors = props.errors || {}

  return (
    <AppLayout>
      <Paper sx={{ p: 4, maxWidth: 700, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Edit App Entry — {vendor.name}</Typography>
          <Button color="error" variant="outlined" onClick={handleDelete}>Delete</Button>
        </Box>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            error={!!errors.name}
            helperText={errors.name?.join(', ')}
          />
          <TextField
            label="Short Description"
            value={form.short_description}
            onChange={(e) => setForm({ ...form, short_description: e.target.value })}
            helperText={`${form.short_description.length}/300 characters`}
            inputProps={{ maxLength: 300 }}
          />
          <TextField
            label="Description"
            multiline
            rows={6}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <FormControl>
            <InputLabel>Categories</InputLabel>
            <Select
              multiple
              value={categoryIds}
              onChange={(e) => setCategoryIds(e.target.value as number[])}
              input={<OutlinedInput label="Categories" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((id) => {
                    const cat = categories.find((c) => c.id === id)
                    return <Chip key={id} label={cat?.name} size="small" />
                  })}
                </Box>
              )}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={form.visibility === 'protected_entry'}
                onChange={(e) => setForm({ ...form, visibility: e.target.checked ? 'protected_entry' : 'public_entry' })}
              />
            }
            label="Protected (visible only to shared users)"
          />
          {app_entry.logo_url && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar src={app_entry.logo_url} variant="rounded" sx={{ width: 64, height: 64 }} />
              <Typography variant="body2" color="text.secondary">Current logo</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>Update Logo</Typography>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => setLogo(e.target.files?.[0] || null)}
            />
          </Box>
          {app_entry.screenshots && app_entry.screenshots.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Current Screenshots</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {app_entry.screenshots.map((s) => (
                  <Box key={s.id} sx={{ position: 'relative' }}>
                    <img src={s.url} alt="Screenshot" style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 4 }} />
                    <IconButton
                      size="small"
                      sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                      onClick={() => handleDeleteScreenshot(s.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>Add Screenshots</Typography>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              onChange={(e) => setScreenshots(e.target.files)}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => router.visit(`/vendors/${vendor.slug}`)}>Cancel</Button>
            <Button type="submit" variant="contained">Save Changes</Button>
          </Box>
        </Box>
      </Paper>
    </AppLayout>
  )
}
